import React, { useState, useMemo, useEffect, useRef } from "react";
import { FaChevronDown } from "react-icons/fa";
import { useLocation, useNavigate } from "react-router-dom";
import axiosInstance from "../components/api/axiosInstance";
import CustomersTable from "./CustomersTable";
import { SupportSortableHeader } from "./SupportSortableHeader";
import TablePagination, { DEFAULT_ROWS_PER_PAGE_OPTIONS } from "../components/TablePagination";
import "../assets/css/DigixLeads.css";

const CUSTOMERS_ROWS_PER_PAGE_KEY = "customersRowsPerPage";

/** Map table "Created On" display (e.g. en-GB dd/mm/yyyy) to YYYY-MM-DD for comparisons. */
function customerCreatedDateToISO(createdAtDisplay) {
  if (!createdAtDisplay || createdAtDisplay === "—") return "";
  const datePart = String(createdAtDisplay).split(" ")[0] || "";
  if (datePart.includes("-") && datePart.length >= 10) return datePart.substring(0, 10);
  const [d, m, y] = datePart.split("/");
  if (y && m && d) {
    return `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
  }
  return "";
}

/**
 * Shared API helper for searching customers.
 * Used by both this page component and the DigixLeads container.
 */
export async function searchCustomersApi(filters, servicesData, allCities) {
  const params = new URLSearchParams();

  const nameOrPhoneValue = (filters.nameOrPhone || "").trim();
  if (nameOrPhoneValue) {
    const digitsOnly = nameOrPhoneValue.replace(/\D/g, "");
    const hasDigits = digitsOnly.length > 0;
    if (hasDigits) {
      params.append("phone", nameOrPhoneValue);
    } else {
      params.append("name", nameOrPhoneValue);
    }
  }

  if (filters.status && String(filters.status).trim()) {
    params.append("project_status", String(filters.status).trim());
  }

  if (filters.service && filters.service !== "All Services") {
    for (const category of servicesData || []) {
      if (category.subcategories && Array.isArray(category.subcategories)) {
        const subcategory = category.subcategories.find(
          (sub) => sub.subcategory_name === filters.service
        );
        if (subcategory && subcategory.subcategory_id) {
          params.append("service_id", subcategory.subcategory_id);
          break;
        }
      }
    }
  }

  if (filters.city) {
    const cityObj = (allCities || []).find(
      (c) =>
        c.city_name?.toLowerCase() === filters.city.toLowerCase() ||
        c.City_Name?.toLowerCase() === filters.city.toLowerCase()
    );
    if (cityObj) {
      const cityId = cityObj.city_id || cityObj.City_Id;
      if (cityId) {
        params.append("city_id", cityId);
      }
    }
  }

  if (filters.domain) {
    params.append("domain", filters.domain.trim());
  }

  if (filters.customerSource) {
    params.append("source", filters.customerSource.trim());
  }

  if (filters.nextFollowupDate) {
    const dateValue = filters.nextFollowupDate.trim();
    if (dateValue) {
      params.append("next_followup_date", dateValue);
    }
  }

  if (filters.createdFrom) {
    const v = filters.createdFrom.trim();
    if (v) params.append("created_from", v);
  }
  if (filters.createdTo) {
    const v = filters.createdTo.trim();
    if (v) params.append("created_to", v);
  }

  const response = await axiosInstance.get(
    `/search-clients?${params.toString()}`
  );

  if (!response.data || !response.data.results) {
    return [];
  }

  return response.data.results.map((item) => {
    const customer = item.client;
    const address = item.address || {};
    const followups = item.followups || [];
    const latestFollowup =
      followups.length > 0 ? followups[followups.length - 1] : null;
    let nextFollowupDateStr = "—";
    let lastFollowupDateStr = "—";
    let lastFollowupNotesStr = "—";

    if (latestFollowup) {
      if (latestFollowup.Next_Followup_Date) {
        const date = new Date(latestFollowup.Next_Followup_Date);
        if (!isNaN(date.getTime())) {
          const day = String(date.getDate()).padStart(2, "0");
          const month = String(date.getMonth() + 1).padStart(2, "0");
          const year = date.getFullYear();
          nextFollowupDateStr = `${day}/${month}/${year}`;
        }
      }
      if (latestFollowup.Followup_Date) {
        const date = new Date(latestFollowup.Followup_Date);
        if (!isNaN(date.getTime())) {
          const day = String(date.getDate()).padStart(2, "0");
          const month = String(date.getMonth() + 1).padStart(2, "0");
          const year = date.getFullYear();
          lastFollowupDateStr = `${day}/${month}/${year}`;
        }
      }
      if (latestFollowup.Remarks) {
        lastFollowupNotesStr = latestFollowup.Remarks;
      }
    }

    return {
      id: customer.client_id,
      name: `${customer.First_Name || ""} ${
        customer.Last_Name || ""
      }`.trim(),
      customerType: customer.Customer_Type || customer.customer_type || "—",
      email: customer.Email || "—",
      phone: customer.Phone || "—",
      companyName: customer.Company_Name || "—",
      domainId: customer.Domain_Id ?? null,
      domain: customer.Domain || "—",
      companyEmail: customer.Company_Email || "—",
      companyPhone: customer.Company_Phone || "—",
      location: address.City_Name || "—",
      services: customer.Services || [],
      status: customer.Status || "—",
      source: customer.Source || "—",
      createdAt: customer.Created_On
        ? new Date(customer.Created_On).toLocaleDateString("en-GB")
        : "—",
      lastFollowupDate: lastFollowupDateStr,
      notes: lastFollowupNotesStr,
      nextFollowupDate: nextFollowupDateStr,
      isQuoteSent: customer.Is_Quote_Sent ? "Yes" : "No",
      customerNotes: customer.Notes || customer.notes || "—",
    };
  });
}

/**
 * CustomersLeadsPage
 *
 * Encapsulates filter state, search logic, free search, and pagination
 * for the Customers tab.
 */
function CustomersLeadsPage({
  customers = [],
  services = [],
  servicesData = [],
  allCities = [],
  loadingCities = false,
  domains = [],
  /** Same source as Projects: `/project-statuses` — drives Status filter dropdown. */
  projectStatuses = [],
  loadingProjectStatuses = false,
  /** Projects list for filtering customers by project status (GET /projects). */
  projects = [],
  onViewLead,
  onEditLead,
  onDeleteCustomer,
  onAddCustomerClick,
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const CUSTOMERS_CURRENT_PAGE_KEY = "customersCurrentPage";

  const [filters, setFilters] = useState({
    nameOrPhone: "",
    city: "",
    domain: "",
    service: "All Services",
    status: "",
    customerSource: "",
    nextFollowupDate: "",
    createdFrom: "",
    createdTo: "",
    freeSearch: "",
  });

  const [searchResults, setSearchResults] = useState(null);
  const [appliedFilters, setAppliedFilters] = useState({
    nameOrPhone: "",
    city: "",
    domain: "",
    service: "All Services",
    status: "",
    customerSource: "",
    nextFollowupDate: "",
    createdFrom: "",
    createdTo: "",
    freeSearch: "",
  });
  const searchRequestSeqRef = useRef(0);
  const skipPageResetOnFiltersSyncRef = useRef(true);
  const [currentPage, setCurrentPage] = useState(() => {
    try {
      const saved = localStorage.getItem(CUSTOMERS_CURRENT_PAGE_KEY);
      if (!saved) return 1;
      const parsed = parseInt(saved, 10);
      return parsed > 0 ? parsed : 1;
    } catch {
      return 1;
    }
  });
  const [rowsPerPage, setRowsPerPage] = useState(() => {
    try {
      const saved = localStorage.getItem(CUSTOMERS_ROWS_PER_PAGE_KEY);
      const parsed = parseInt(saved, 10);
      if (DEFAULT_ROWS_PER_PAGE_OPTIONS.includes(parsed)) {
        return parsed;
      }
    } catch {
      /* ignore */
    }
    return 5;
  });
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");
  const [columnWidths, setColumnWidths] = useState({});
  const resizeRef = React.useRef({ startX: 0, startWidth: 0, columnIndex: null });
  const [domainDropdownOpen, setDomainDropdownOpen] = useState(false);
  const [domainSearchQuery, setDomainSearchQuery] = useState("");
  const domainDropdownRef = useRef(null);
  const [serviceDropdownOpen, setServiceDropdownOpen] = useState(false);
  const [serviceSearchQuery, setServiceSearchQuery] = useState("");
  const serviceDropdownRef = useRef(null);
  const [locationDropdownOpen, setLocationDropdownOpen] = useState(false);
  const [locationSearchQuery, setLocationSearchQuery] = useState("");
  const locationDropdownRef = useRef(null);

  useEffect(() => {
    const preset = location.state?.dashboardCustomerStatusPreset;
    if (typeof preset !== "string" || !preset) return;
    const normalizedPreset = preset.toLowerCase();
    if (normalizedPreset !== "active" && normalizedPreset !== "activeprojects") return;
    setFilters((prev) => ({ ...prev, status: "Active" }));
    setCurrentPage(1);
    navigate(location.pathname, { replace: true, state: null });
  }, [location.state, location.pathname, navigate]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (domainDropdownRef.current && !domainDropdownRef.current.contains(e.target)) {
        setDomainDropdownOpen(false);
        setDomainSearchQuery("");
      }
      if (serviceDropdownRef.current && !serviceDropdownRef.current.contains(e.target)) {
        setServiceDropdownOpen(false);
        setServiceSearchQuery("");
      }
      if (locationDropdownRef.current && !locationDropdownRef.current.contains(e.target)) {
        setLocationDropdownOpen(false);
        setLocationSearchQuery("");
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const emptyFilters = {
    nameOrPhone: "",
    city: "",
    domain: "",
    service: "All Services",
    status: "",
    customerSource: "",
    nextFollowupDate: "",
    createdFrom: "",
    createdTo: "",
    freeSearch: "",
  };

  const handleClearFilters = () => {
    setFilters(emptyFilters);
    setAppliedFilters(emptyFilters);
    setSearchResults(null);
    setCurrentPage(1);
    setDomainDropdownOpen(false);
    setServiceDropdownOpen(false);
    setLocationDropdownOpen(false);
    setDomainSearchQuery("");
    setServiceSearchQuery("");
    setLocationSearchQuery("");
  };

  // Persist pagination page so View/Edit -> Back returns to the same page.
  useEffect(() => {
    try {
      localStorage.setItem(CUSTOMERS_CURRENT_PAGE_KEY, String(currentPage));
    } catch {
      /* ignore storage errors */
    }
  }, [currentPage]);

  const hasSearchFilters = (f) => {
    return (
      f.nameOrPhone ||
      f.city ||
      (f.status && String(f.status).trim()) ||
      (f.service && f.service !== "All Services") ||
      f.domain ||
      f.customerSource ||
      f.nextFollowupDate ||
      f.createdFrom ||
      f.createdTo
    );
  };

  useEffect(() => {
    setAppliedFilters(filters);
    if (skipPageResetOnFiltersSyncRef.current) {
      skipPageResetOnFiltersSyncRef.current = false;
    } else {
      setCurrentPage(1);
    }
  }, [filters]);

  useEffect(() => {
    const seq = ++searchRequestSeqRef.current;
    const t = setTimeout(async () => {
      if (!hasSearchFilters(filters)) {
        if (seq === searchRequestSeqRef.current) {
          setSearchResults(null);
        }
        return;
      }
      try {
        const results = await searchCustomersApi(filters, servicesData, allCities);
        if (seq === searchRequestSeqRef.current) {
          setSearchResults(results);
        }
      } catch (error) {
        const msg =
          error.response?.data?.error ||
          error.message ||
          "Error searching customers.";
        console.error("Error searching customers:", msg);
        if (seq === searchRequestSeqRef.current) {
          setSearchResults([]);
          alert(
            "Error searching customers: " +
              (error.response?.data?.error || error.message)
          );
        }
      }
    }, 350);
    return () => clearTimeout(t);
  }, [filters, servicesData, allCities]);

  const baseCustomers = useMemo(
    () => (searchResults !== null ? searchResults : customers),
    [searchResults, customers]
  );

  const customerIdsWithSelectedProjectStatus = useMemo(() => {
    const name = appliedFilters.status;
    if (!name || !String(name).trim() || !Array.isArray(projects) || projects.length === 0) {
      return null;
    }
    const needle = String(name).trim().toLowerCase();
    const ids = new Set();
    for (const p of projects) {
      const st = String(
        p?.Status ?? p?.status ?? p?.Status_Name ?? p?.status_name ?? ""
      )
        .trim()
        .toLowerCase();
      if (st === needle) {
        const cid = Number(p?.client_id ?? p?.client_id ?? p?.CustomerID ?? p?.customerId);
        if (Number.isFinite(cid) && cid > 0) ids.add(cid);
      }
    }
    return ids;
  }, [appliedFilters.status, projects]);

  const filteredCustomers = useMemo(() => {
    const af = appliedFilters;
    return baseCustomers.filter((lead) => {
      if (af.nameOrPhone && searchResults === null) {
        const searchValue = af.nameOrPhone.toLowerCase().trim();
        const nameMatch = lead.name?.toLowerCase().includes(searchValue);
        const phoneMatch = lead.phone?.includes(af.nameOrPhone.trim());
        if (!nameMatch && !phoneMatch) return false;
      }
      if (
        af.city &&
        !lead.location?.toLowerCase().includes(af.city.toLowerCase())
      ) {
        return false;
      }
      if (
        af.domain &&
        !lead.domain?.toLowerCase().includes(af.domain.toLowerCase())
      ) {
        return false;
      }
      if (
        af.service &&
        af.service !== "All Services" &&
        !lead.services?.includes(af.service)
      ) {
        return false;
      }
      if (customerIdsWithSelectedProjectStatus !== null) {
        const leadId = Number(lead.id);
        if (!customerIdsWithSelectedProjectStatus.has(leadId)) return false;
      }
      if (af.nextFollowupDate) {
        const filterDate = af.nextFollowupDate; // YYYY-MM-DD
        if (!lead.nextFollowupDate || lead.nextFollowupDate === "—") {
          return false;
        }
        const datePart = lead.nextFollowupDate.split(" ")[0] || "";
        let leadDateISO = "";
        if (datePart.includes("-") && datePart.length >= 10) {
          leadDateISO = datePart.substring(0, 10);
        } else {
          const [d, m, y] = datePart.split("/");
          if (y && m && d)
            leadDateISO = `${y}-${m.padStart(2, "0")}-${d.padStart(
              2,
              "0"
            )}`;
        }
        if (leadDateISO !== filterDate) return false;
      }
      if (af.createdFrom) {
        const leadISO = customerCreatedDateToISO(lead.createdAt);
        if (!leadISO || leadISO < af.createdFrom) return false;
      }
      if (af.createdTo) {
        const leadISO = customerCreatedDateToISO(lead.createdAt);
        if (!leadISO || leadISO > af.createdTo) return false;
      }
      return true;
    });
  }, [baseCustomers, appliedFilters, searchResults, customerIdsWithSelectedProjectStatus]);

  const freeSearchFiltered = useMemo(() => {
    if (!appliedFilters.freeSearch) return filteredCustomers;
    const searchValue = appliedFilters.freeSearch.toLowerCase().trim();
    if (!searchValue) return filteredCustomers;

    return filteredCustomers.filter((lead) => {
      const name = (lead.name || "").toLowerCase();
      const email = (lead.email || "").toLowerCase();
      const phone = (lead.phone || "").toLowerCase();
      const location = (lead.location || "").toLowerCase();
      const createdAt = (lead.createdAt || "").toLowerCase();
      const lastFollowupDate = (lead.lastFollowupDate || "").toLowerCase();
      const notes = (lead.notes || "").toLowerCase();
      const nextFollowupDate = (lead.nextFollowupDate || "").toLowerCase();
      const status = (lead.status || "").toLowerCase();
      const source = (lead.source || "").toLowerCase();
      const servicesStr = Array.isArray(lead.services)
        ? lead.services.join(", ").toLowerCase()
        : (lead.services || "").toLowerCase();
      const customerType = (lead.customerType || "").toLowerCase();

      return (
        name.includes(searchValue) ||
        customerType.includes(searchValue) ||
        email.includes(searchValue) ||
        phone.includes(searchValue) ||
        location.includes(searchValue) ||
        createdAt.includes(searchValue) ||
        lastFollowupDate.includes(searchValue) ||
        notes.includes(searchValue) ||
        nextFollowupDate.includes(searchValue) ||
        status.includes(searchValue) ||
        source.includes(searchValue) ||
        servicesStr.includes(searchValue)
      );
    });
  }, [filteredCustomers, appliedFilters.freeSearch]);

  const totalPages = Math.max(1, Math.ceil(freeSearchFiltered.length / rowsPerPage));

  const getSortValue = (lead, columnId) => {
    switch (columnId) {
      case "name":
        return String(lead.name || "").toLowerCase();
      case "customerType":
        return String(lead.customerType || "").toLowerCase();
      case "phone":
        return String(lead.phone || "").toLowerCase();
      case "location":
        return String(lead.location || "").toLowerCase();
      case "services": {
        const servicesStr = Array.isArray(lead.services) ? lead.services.join(", ") : (lead.services || "");
        return String(servicesStr).toLowerCase();
      }
      case "domain":
        return String(lead.domain || "").toLowerCase();
      case "status":
        return String(lead.status || "").toLowerCase();
      case "isQuoteSent":
        return String(lead.isQuoteSent || "").toLowerCase();
      case "notes":
        return String(lead.customerNotes || lead.notes || "").toLowerCase();
      case "lastFollowupDate":
      case "nextFollowupDate":
      case "createdAt": {
        const raw = columnId === "lastFollowupDate" ? lead.lastFollowupDate : columnId === "nextFollowupDate" ? lead.nextFollowupDate : lead.createdAt;
        const iso = customerCreatedDateToISO(raw);
        const ts = iso ? new Date(iso).getTime() : 0;
        return Number.isFinite(ts) ? ts : 0;
      }
      case "followupNotes":
        return String(lead.notes || "").toLowerCase();
      case "source":
        return String(lead.source || "").toLowerCase();
      case "companyName":
        return String(lead.companyName || "").toLowerCase();
      case "email":
        return String(lead.companyEmail || lead.email || "").toLowerCase();
      default:
        return "";
    }
  };

  const sortedCustomers = useMemo(() => {
    if (!sortColumn) return freeSearchFiltered;
    const mult = sortDirection === "desc" ? -1 : 1;
    return [...freeSearchFiltered].sort((a, b) => {
      const va = getSortValue(a, sortColumn);
      const vb = getSortValue(b, sortColumn);
      if (va < vb) return -1 * mult;
      if (va > vb) return 1 * mult;
      return Number(a.id || 0) - Number(b.id || 0);
    });
  }, [freeSearchFiltered, sortColumn, sortDirection]);

  // Clamp page when the data set changes (e.g., after returning from View/Edit).
  useEffect(() => {
    if (totalPages > 0 && currentPage > totalPages) setCurrentPage(1);
  }, [totalPages, currentPage]);

  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const currentLeads = sortedCustomers.slice(startIndex, endIndex);

  const handleRowsPerPageChange = (next) => {
    setRowsPerPage(next);
    setCurrentPage(1);
    localStorage.setItem(CUSTOMERS_ROWS_PER_PAGE_KEY, String(next));
  };

  const handleSort = (columnId) => {
    setCurrentPage(1);
    if (sortColumn === columnId) {
      setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(columnId);
      setSortDirection("asc");
    }
  };

  const getColumnWidth = (columnIndex) => {
    if (columnWidths[columnIndex]) {
      return `${columnWidths[columnIndex]}px`;
    }
    return undefined;
  };

  const getColumnMinWidth = (columnIndex) => {
    if (columnWidths[columnIndex]) {
      return `${columnWidths[columnIndex]}px`;
    }
    return undefined;
  };

  const handleResizeStart = (e, columnIndex) => {
    e.preventDefault();
    e.stopPropagation();

    const th = e.currentTarget.parentElement;
    if (!th) return;

    const startWidth = th.offsetWidth;
    const startX = e.clientX;

    resizeRef.current = {
      startX,
      startWidth,
      columnIndex
    };

    document.body.classList.add("resizing");

    const handleMouseMove = (moveEvent) => {
      if (resizeRef.current.columnIndex === null) return;
      const diff = moveEvent.clientX - resizeRef.current.startX;
      const newWidth = Math.max(80, resizeRef.current.startWidth + diff);

      setColumnWidths((prev) => ({
        ...prev,
        [resizeRef.current.columnIndex]: newWidth
      }));
    };

    const handleMouseUp = () => {
      resizeRef.current = { startX: 0, startWidth: 0, columnIndex: null };
      document.body.classList.remove("resizing");
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  return (
    <div className="customers-page">
      {/* Filters (left), New Customer (right); API search runs while typing (debounced). */}
      <div className="search-actions-row">
        <div className="search-filters-row search-filters-row--customers-dates">
          <input
            type="text"
            className="search-filter-input customers-filter-name-phone"
            placeholder="Search name or phone"
            value={filters.nameOrPhone}
            onChange={(e) => handleFilterChange("nameOrPhone", e.target.value)}
          />
          <div className="domain-filter-dropdown" ref={locationDropdownRef}>
            <div
              className="search-filter-input search-filter-select domain-filter-trigger"
              onClick={() => !loadingCities && setLocationDropdownOpen((o) => !o)}
              role="button"
              tabIndex={loadingCities ? -1 : 0}
              onKeyDown={(e) => {
                if (!loadingCities && (e.key === "Enter" || e.key === " ")) {
                  e.preventDefault();
                  setLocationDropdownOpen((o) => !o);
                }
              }}
              style={{ opacity: loadingCities ? 0.6 : 1, cursor: loadingCities ? "default" : "pointer" }}
            >
              <span>{loadingCities ? "Loading locations..." : (filters.city || "All Locations")}</span>
              <FaChevronDown className={`domain-filter-chevron ${locationDropdownOpen ? "open" : ""}`} />
            </div>
            {locationDropdownOpen && !loadingCities && (
              <div className="domain-filter-panel">
                <input
                  type="text"
                  className="domain-filter-search"
                  placeholder="Search locations..."
                  value={locationSearchQuery}
                  onChange={(e) => setLocationSearchQuery(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  autoFocus
                />
                <div className="domain-filter-options">
                  <div
                    className={`domain-filter-option ${!filters.city ? "selected" : ""}`}
                    onClick={() => {
                      handleFilterChange("city", "");
                      setLocationDropdownOpen(false);
                      setLocationSearchQuery("");
                    }}
                  >
                    All Locations
                  </div>
                  {(() => {
                    const sortedCities = [...allCities].sort((a, b) =>
                      String(a.city_name || a.City_Name || "").localeCompare(String(b.city_name || b.City_Name || ""))
                    );
                    const filteredCities = sortedCities.filter((c) => {
                      const name = c.city_name || c.City_Name || "";
                      return !locationSearchQuery.trim() || String(name).toLowerCase().includes(locationSearchQuery.trim().toLowerCase());
                    });
                    return filteredCities.length > 0 ? (
                      filteredCities.map((c) => {
                        const cityName = c.city_name || c.City_Name || "";
                        return (
                          <div
                            key={c.city_id}
                            className={`domain-filter-option ${filters.city === cityName ? "selected" : ""}`}
                            onClick={() => {
                              handleFilterChange("city", cityName);
                              setLocationDropdownOpen(false);
                              setLocationSearchQuery("");
                            }}
                          >
                            {cityName}
                          </div>
                        );
                      })
                    ) : (
                      <div className="domain-filter-no-results">No locations found</div>
                    );
                  })()}
                </div>
              </div>
            )}
          </div>
          <div className="domain-filter-dropdown" ref={serviceDropdownRef}>
            <div
              className="search-filter-input search-filter-select domain-filter-trigger"
              onClick={() => setServiceDropdownOpen((o) => !o)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setServiceDropdownOpen((o) => !o);
                }
              }}
            >
              <span>{filters.service === "All Services" || !filters.service ? "All Services" : filters.service}</span>
              <FaChevronDown className={`domain-filter-chevron ${serviceDropdownOpen ? "open" : ""}`} />
            </div>
            {serviceDropdownOpen && (
              <div className="domain-filter-panel">
                <input
                  type="text"
                  className="domain-filter-search"
                  placeholder="Search services..."
                  value={serviceSearchQuery}
                  onChange={(e) => setServiceSearchQuery(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  autoFocus
                />
                <div className="domain-filter-options">
                  <div
                    className={`domain-filter-option ${!filters.service || filters.service === "All Services" ? "selected" : ""}`}
                    onClick={() => {
                      handleFilterChange("service", "All Services");
                      setServiceDropdownOpen(false);
                      setServiceSearchQuery("");
                    }}
                  >
                    All Services
                  </div>
                  {(() => {
                    const filteredServices = services.filter(
                      (s) =>
                        s === "All Services" ||
                        !serviceSearchQuery.trim() ||
                        String(s).toLowerCase().includes(serviceSearchQuery.trim().toLowerCase())
                    );
                    const filteredWithoutAll = filteredServices.filter((s) => s !== "All Services");
                    return filteredWithoutAll.length > 0 ? (
                      filteredWithoutAll.map((s) => (
                        <div
                          key={s}
                          className={`domain-filter-option ${filters.service === s ? "selected" : ""}`}
                          onClick={() => {
                            handleFilterChange("service", s);
                            setServiceDropdownOpen(false);
                            setServiceSearchQuery("");
                          }}
                        >
                          {s}
                        </div>
                      ))
                    ) : (
                      <div className="domain-filter-no-results">No services found</div>
                    );
                  })()}
                </div>
              </div>
            )}
          </div>
          <div className="domain-filter-dropdown" ref={domainDropdownRef}>
            <div
              className="search-filter-input search-filter-select domain-filter-trigger"
              onClick={() => setDomainDropdownOpen((o) => !o)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setDomainDropdownOpen((o) => !o);
                }
              }}
            >
              <span>{filters.domain || "All businesses"}</span>
              <FaChevronDown className={`domain-filter-chevron ${domainDropdownOpen ? "open" : ""}`} />
            </div>
            {domainDropdownOpen && (
              <div className="domain-filter-panel">
                <input
                  type="text"
                  className="domain-filter-search"
                  placeholder="Search businesses..."
                  value={domainSearchQuery}
                  onChange={(e) => setDomainSearchQuery(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  autoFocus
                />
                <div className="domain-filter-options">
                  <div
                    className={`domain-filter-option ${!filters.domain ? "selected" : ""}`}
                    onClick={() => {
                      handleFilterChange("domain", "");
                      setDomainDropdownOpen(false);
                      setDomainSearchQuery("");
                    }}
                  >
                    All businesses
                  </div>
                  {(() => {
                    const filteredDomains = domains
                      .map((d) => d.name)
                      .filter((name) => !domainSearchQuery.trim() || String(name || "").toLowerCase().includes(domainSearchQuery.trim().toLowerCase()))
                      .sort();
                    return filteredDomains.length > 0 ? (
                      filteredDomains.map((dom) => (
                        <div
                          key={dom}
                          className={`domain-filter-option ${filters.domain === dom ? "selected" : ""}`}
                          onClick={() => {
                            handleFilterChange("domain", dom);
                            setDomainDropdownOpen(false);
                            setDomainSearchQuery("");
                          }}
                        >
                          {dom}
                        </div>
                      ))
                    ) : (
                      <div className="domain-filter-no-results">No businesses found</div>
                    );
                  })()}
                </div>
              </div>
            )}
          </div>
          <label className="customers-date-filter-field">
            <span className="customers-date-filter-field__label">Created from</span>
            <input
              type="date"
              className="search-filter-input"
              title="Created from"
              value={filters.createdFrom}
              onChange={(e) => handleFilterChange("createdFrom", e.target.value)}
            />
          </label>
          <label className="customers-date-filter-field">
            <span className="customers-date-filter-field__label">Created to</span>
            <input
              type="date"
              className="search-filter-input"
              title="Created to"
              value={filters.createdTo}
              onChange={(e) => handleFilterChange("createdTo", e.target.value)}
            />
          </label>
          <select
            className="search-filter-input search-filter-select"
            title="Filter customers by project status"
            value={filters.status || ""}
            onChange={(e) => handleFilterChange("status", e.target.value)}
          >
            <option value="">All Statuses</option>
            {!loadingProjectStatuses &&
              projectStatuses.map((s) => (
                <option key={s.id} value={s.name}>
                  {s.name}
                </option>
              ))}
          </select>
          <button
            type="button"
            className="btn-clear"
            onClick={handleClearFilters}
          >
            Clear
          </button>
        </div>
        <div className="header-actions">
          {onAddCustomerClick && (
            <button
              type="button"
              className="btn-primary btn-add-lead"
              onClick={onAddCustomerClick}
            >
              New Client
            </button>
          )}
        </div>
      </div>

      {/* Search across columns - below filter row */}
      <div className="search-row-2">
        <div className="free-search-bar">
          <input
            type="text"
            className="free-search-input"
            placeholder="Search..."
            value={filters.freeSearch}
            onChange={(e) => handleFilterChange("freeSearch", e.target.value)}
            title="Search across all columns"
          />
        </div>
      </div>

      <div className="table-wrapper">
        <div className="table-container">
          <table className="data-table leads-table-frozen">
            <thead>
              <tr>
                <th
                  className="col-sticky col-0 digix-th-sortable"
                  style={{
                    minWidth: getColumnMinWidth(0) || "140px",
                    width: getColumnWidth(0)
                  }}
                >
                  <div className="digix-th-sort-row">
                    <SupportSortableHeader columnId="name" label="Full Name" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                  </div>
                  <span
                    className="column-resizer"
                    onMouseDown={(e) => handleResizeStart(e, 0)}
                  ></span>
                </th>
                <th
                  className="digix-th-sortable"
                  style={{
                    minWidth: getColumnMinWidth(1) || "120px",
                    width: getColumnWidth(1),
                    position: "relative"
                  }}
                >
                  <div className="digix-th-sort-row"><SupportSortableHeader columnId="customerType" label="Customer type" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} /></div>
                  <span
                    className="column-resizer"
                    onMouseDown={(e) => handleResizeStart(e, 1)}
                  ></span>
                </th>
                <th
                  className="digix-th-sortable"
                  style={{
                    minWidth: getColumnMinWidth(2) || "110px",
                    width: getColumnWidth(2),
                    position: "relative"
                  }}
                >
                  <div className="digix-th-sort-row"><SupportSortableHeader columnId="phone" label="Contact" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} /></div>
                  <span
                    className="column-resizer"
                    onMouseDown={(e) => handleResizeStart(e, 2)}
                  ></span>
                </th>
                <th
                  className="digix-th-sortable"
                  style={{
                    minWidth: getColumnMinWidth(3) || "120px",
                    width: getColumnWidth(3),
                    position: "relative"
                  }}
                >
                  <div className="digix-th-sort-row"><SupportSortableHeader columnId="location" label="Location" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} /></div>
                  <span
                    className="column-resizer"
                    onMouseDown={(e) => handleResizeStart(e, 3)}
                  ></span>
                </th>
                <th
                  className="digix-th-sortable"
                  style={{
                    minWidth: getColumnMinWidth(4) || "140px",
                    width: getColumnWidth(4),
                    position: "relative"
                  }}
                >
                  <div className="digix-th-sort-row"><SupportSortableHeader columnId="services" label="Services" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} /></div>
                  <span
                    className="column-resizer"
                    onMouseDown={(e) => handleResizeStart(e, 4)}
                  ></span>
                </th>
                <th
                  className="digix-th-sortable"
                  style={{
                    minWidth: getColumnMinWidth(5) || "120px",
                    width: getColumnWidth(5),
                    position: "relative"
                  }}
                >
                  <div className="digix-th-sort-row"><SupportSortableHeader columnId="domain" label="Business" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} /></div>
                  <span
                    className="column-resizer"
                    onMouseDown={(e) => handleResizeStart(e, 5)}
                  ></span>
                </th>
                <th
                  className="digix-th-sortable"
                  style={{
                    minWidth: getColumnMinWidth(6) || "110px",
                    width: getColumnWidth(6),
                    position: "relative"
                  }}
                >
                  <div className="digix-th-sort-row"><SupportSortableHeader columnId="status" label="Status" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} /></div>
                  <span
                    className="column-resizer"
                    onMouseDown={(e) => handleResizeStart(e, 6)}
                  ></span>
                </th>
                <th
                  className="digix-th-sortable"
                  style={{
                    minWidth: getColumnMinWidth(7) || "110px",
                    width: getColumnWidth(7),
                    position: "relative"
                  }}
                >
                  <div className="digix-th-sort-row"><SupportSortableHeader columnId="isQuoteSent" label="Quote Sent" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} /></div>
                  <span
                    className="column-resizer"
                    onMouseDown={(e) => handleResizeStart(e, 7)}
                  ></span>
                </th>
                <th
                  className="digix-th-sortable"
                  style={{
                    minWidth: getColumnMinWidth(8) || "130px",
                    width: getColumnWidth(8),
                    position: "relative"
                  }}
                >
                  <div className="digix-th-sort-row"><SupportSortableHeader columnId="notes" label="Notes" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} /></div>
                  <span
                    className="column-resizer"
                    onMouseDown={(e) => handleResizeStart(e, 8)}
                  ></span>
                </th>
                <th
                  className="digix-th-sortable"
                  style={{
                    minWidth: getColumnMinWidth(9) || "130px",
                    width: getColumnWidth(9),
                    position: "relative"
                  }}
                >
                  <div className="digix-th-sort-row"><SupportSortableHeader columnId="lastFollowupDate" label="Follow-up Date" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} /></div>
                  <span
                    className="column-resizer"
                    onMouseDown={(e) => handleResizeStart(e, 9)}
                  ></span>
                </th>
                <th
                  className="digix-th-sortable"
                  style={{
                    minWidth: getColumnMinWidth(10) || "150px",
                    width: getColumnWidth(10),
                    position: "relative"
                  }}
                >
                  <div className="digix-th-sort-row"><SupportSortableHeader columnId="followupNotes" label="Follow-up Notes" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} /></div>
                  <span
                    className="column-resizer"
                    onMouseDown={(e) => handleResizeStart(e, 10)}
                  ></span>
                </th>
                <th
                  className="digix-th-sortable"
                  style={{
                    minWidth: getColumnMinWidth(11) || "150px",
                    width: getColumnWidth(11),
                    position: "relative"
                  }}
                >
                  <div className="digix-th-sort-row"><SupportSortableHeader columnId="nextFollowupDate" label="Next Follow-up Date" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} /></div>
                  <span
                    className="column-resizer"
                    onMouseDown={(e) => handleResizeStart(e, 11)}
                  ></span>
                </th>
                <th
                  className="digix-th-sortable"
                  style={{
                    minWidth: getColumnMinWidth(12) || "130px",
                    width: getColumnWidth(12),
                    position: "relative"
                  }}
                >
                  <div className="digix-th-sort-row"><SupportSortableHeader columnId="createdAt" label="Created On" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} /></div>
                  <div className="digix-th-date-format-hint">(dd/mm/yyyy)</div>
                  <span
                    className="column-resizer"
                    onMouseDown={(e) => handleResizeStart(e, 12)}
                  ></span>
                </th>
                <th
                  className="digix-th-sortable"
                  style={{
                    minWidth: getColumnMinWidth(13) || "130px",
                    width: getColumnWidth(13),
                    position: "relative"
                  }}
                >
                  <div className="digix-th-sort-row"><SupportSortableHeader columnId="source" label="Source" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} /></div>
                  <span
                    className="column-resizer"
                    onMouseDown={(e) => handleResizeStart(e, 13)}
                  ></span>
                </th>
                <th
                  className="digix-th-sortable"
                  style={{
                    minWidth: getColumnMinWidth(14) || "150px",
                    width: getColumnWidth(14),
                    position: "relative"
                  }}
                >
                  <div className="digix-th-sort-row"><SupportSortableHeader columnId="companyName" label="Company Name" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} /></div>
                  <span
                    className="column-resizer"
                    onMouseDown={(e) => handleResizeStart(e, 14)}
                  ></span>
                </th>
                <th
                  className="digix-th-sortable"
                  style={{
                    minWidth: getColumnMinWidth(15) || "140px",
                    width: getColumnWidth(15),
                    position: "relative"
                  }}
                >
                  <div className="digix-th-sort-row"><SupportSortableHeader columnId="email" label="Email" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} /></div>
                  <span
                    className="column-resizer"
                    onMouseDown={(e) => handleResizeStart(e, 15)}
                  ></span>
                </th>
                <th
                  className="col-sticky col-action"
                  style={{
                    minWidth: getColumnMinWidth(16) || "120px",
                    width: getColumnWidth(16)
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>Action</div>
                  <span
                    className="column-resizer"
                    onMouseDown={(e) => handleResizeStart(e, 16)}
                  ></span>
                </th>
              </tr>
            </thead>
            <tbody>
              <CustomersTable
                rows={currentLeads}
                courses={[]}
                servicesData={servicesData}
                domains={domains}
                getColumnWidth={getColumnWidth}
                getColumnMinWidth={getColumnMinWidth}
                handleView={(id) => onViewLead && onViewLead(id, "client")}
                handleEdit={(id) => onEditLead && onEditLead(id, "client")}
                handleDelete={onDeleteCustomer}
              />
            </tbody>
          </table>
        </div>
      </div>

      {sortedCustomers.length > 0 && (
        <TablePagination
          currentPage={currentPage}
          totalItems={sortedCustomers.length}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={DEFAULT_ROWS_PER_PAGE_OPTIONS}
          onPageChange={setCurrentPage}
          onRowsPerPageChange={handleRowsPerPageChange}
          itemLabel="clients"
          rowsPerPageSelectId="customers-rows-per-page"
        />
      )}
    </div>
  );
}

export default CustomersLeadsPage;

